<template>
  <div>
    <p class="body-2 font-weight-bold mb-1">{{ label || 'Date' }}</p>
    <v-layout row align-center>
      <!-- DATE -->
      <v-flex xs8 pt-0>
          <v-text-field v-model="dateInput" label="Date" @input="checkDate" :error="dateError" placeholder="DD/MM/YYYY" v-bind="$attrs" />
      </v-flex>
      <!-- TIME -->
      <v-flex pt-0>
          <v-text-field v-model="timeInput" label="Time" @input="checkTime" :error="timeError" placeholder="HH:MM" v-bind="$attrs" />
      </v-flex>
      <!-- AM/PM (US only) -->
      <v-flex v-if="$country === 'US'" pt-0>
        <v-select v-model="ampmInput" :items="['AM', 'PM']" label="AM/PM" @input="checkTime"
          :disabled="!dateInput || dateError" :error="timeError" v-bind="$attrs" />
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
export default {
  name: 'DateTimeSelector',
  props: ['currentDate', 'label', 'value'],
  data (vm) {
    return {
      dateInput: vm.formatDate(vm.currentDate || vm.value),
      timeInput: vm.formatTime(vm.currentDate || vm.value),
      ampmInput: vm.formatAmpm(vm.currentDate || vm.value),
      dateError: false,
      timeError: false
    }
  },
  methods: {
    clear () {
      this.dateInput = ''
      this.timeInput = ''
      this.ampmInput = ''
      this.dateError = false
      this.timeError = false
      this.$emit('dateSelected', null)
    },
    formatDate (date) {
      if (!date) return null

      const d = new Date(date)
      if (this.$country === 'US') {
        let hours = d.getHours()
        this.ampmInput = hours >= 12 ? 'PM' : 'AM'
        hours = hours % 12
        if (hours === 0) hours = 12
        return `${('0' + (d.getMonth() + 1)).slice(-2)}/${('0' + d.getDate()).slice(-2)}/${d.getFullYear()}`
      } else {
        return `${('0' + d.getDate()).slice(-2)}/${('0' + (d.getMonth() + 1)).slice(-2)}/${d.getFullYear()}`
      }
    },
    formatTime (date) {
      if (!date) return null

      const d = new Date(date)
      if (this.$country === 'US') {
        let hours = d.getHours()
        const minutes = d.getMinutes()
        this.ampmInput = hours >= 12 ? 'PM' : 'AM'
        hours = hours % 12
        if (hours === 0) {
          hours = 12
        }
        return `${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}`
      } else {
        const hours = d.getHours()
        const minutes = d.getMinutes()
        return `${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}`
      }
    },
    formatAmpm (date) {
      if (!date) return ''

      const d = new Date(date)
      return d.getHours() >= 12 ? 'PM' : 'AM'
    },
    validateDateInput () {
      if (!this.dateInput || this.dateInput.length !== 10) return false

      const parts = this.dateInput.split('/')
      if (parts.length !== 3) return false

      let day, month, year
      if (this.$country === 'US') {
        // Expected format : MM/DD/YYYY
        month = parseInt(parts[0], 10)
        day = parseInt(parts[1], 10)
      } else {
        // Expected format : DD/MM/YYYY
        day = parseInt(parts[0], 10)
        month = parseInt(parts[1], 10)
      }
      year = parseInt(parts[2], 10)

      if (parts[2].length !== 4) return false
      if (isNaN(day) || isNaN(month) || isNaN(year)) return false
      if (month < 1 || month > 12) return false
      if (day < 1 || day > 31) return false

      const monthDays = [
        31,
        (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28,
        31, 30, 31, 30, 31, 31, 30, 31, 30, 31
      ]
      if (day > monthDays[month - 1]) return false

      return true
    },
    validateTimeInput () {
      if (!this.timeInput || this.timeInput.length !== 5) return false

      const parts = this.timeInput.split(':')
      if (parts.length !== 2) return false

      let hours = parseInt(parts[0], 10)
      const minutes = parseInt(parts[1], 10)

      if (isNaN(hours) || isNaN(minutes)) return false

      if (this.$country === 'US') {
        if (hours < 1 || hours > 12) return false
      } else {
        if (hours < 0 || hours > 23) return false
      }
      if (minutes < 0 || minutes > 59) return false

      return true
    },

    emitDate () {
      const parts = this.dateInput.split('/')
      let day, month, year
      if (this.$country === 'US') {
        month = parseInt(parts[0], 10)
        day = parseInt(parts[1], 10)
      } else {
        day = parseInt(parts[0], 10)
        month = parseInt(parts[1], 10)
      }
      year = parseInt(parts[2], 10)

      const timeParts = this.timeInput.split(':')
      let hours = parseInt(timeParts[0], 10)
      const minutes = parseInt(timeParts[1], 10)

      if (this.$country === 'US') {
        if (this.ampmInput === 'PM' && hours < 12) {
          hours += 12
        } else if (this.ampmInput === 'AM' && hours === 12) {
          hours = 0
        }
      }

      const newDate = new Date(year, month - 1, day, hours, minutes)
      this.$emit('dateSelected', newDate.toISOString())
      this.$emit('input', newDate.toISOString())
    },

    evaluateAll () {
      this.dateError = false
      this.timeError = false

      if (this.dateInput) {
        if (!this.validateDateInput()) {
          this.dateError = true
        }
      }
      if (this.timeInput) {
        if (!this.validateTimeInput()) {
          this.timeError = true
        }
      }

      if (this.dateInput && !this.timeInput) {
        this.timeError = true
      }
      if (this.timeInput && !this.dateInput) {
        this.dateError = true
      }

      if (this.dateError || this.timeError) {
        return
      }

      this.emitDate()
    },

    checkDate () {
      if (this.validateDateInput() && !this.timeInput) {
        if (this.$country === 'US') {
          this.timeInput = '12:00'
          this.ampmInput = 'AM'
        } else {
          this.timeInput = '00:00'
        }
      }
      this.evaluateAll()
    },
    checkTime () {
      this.evaluateAll()
    }
  },
  computed: {
    dateLabel () {
      return this.$country === 'US'
        ? 'mm/dd/yyyy'
        : 'dd/mm/yyyy'
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/common/DateSelector.vue